import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import block from 'bem-cn-lite';
import {
    Button,
    Icon,
    RadioGroup,
    Tabs,
    Text,
    TextArea,
    TextInput,
    Tooltip,
    Modal
} from '@gravity-ui/uikit';
import {Ban, CaretDown, Plus, Star, StarFill, Xmark} from '@gravity-ui/icons';
import {RowCommentCell} from './RowCommentCell';
import {AddFormKind, ClientPreference, ListTabId} from '@features/profile/types/FavoritePriceLists';
import {useMediaQuery} from 'react-responsive';
import {KeyboardGrid} from '@components/KeyboardNavigation';

import './FavoritePriceLists.scss';

const b = block('favorite-price-lists');

type RemoveModalState = {list: ListTabId; rowId: string} | null;

interface FavoritePriceListsProps {
    listTab: ListTabId;
    onListTabChange: (tab: ListTabId) => void;
    rows: ClientPreference[];
    editingRowId: string | null;
    setEditingRowId: React.Dispatch<React.SetStateAction<string | null>>;
    onChangeComment: (rowId: string, value: string) => void;
    onFinishEdit: (rowId: string) => void;
    onClearComment: (rowId: string) => void;
    onAdd: (data: {kind: AddFormKind; value: string; comment: string}) => Promise<void> | void;
    onRemove: (rowId: string) => Promise<void> | void;
}

export const FavoritePriceLists: React.FC<FavoritePriceListsProps> = ({
    listTab,
    onListTabChange,
    rows,
    editingRowId,
    setEditingRowId,
    onChangeComment,
    onFinishEdit,
    onClearComment,
    onAdd,
    onRemove
}) => {
    const isMobileScreen = useMediaQuery({query: '(max-width: 1150px)'});

    const [removeModal, setRemoveModal] = useState<RemoveModalState>(null);
    const [addPanelOpen, setAddPanelOpen] = useState(false);
    const [addFormKind, setAddFormKind] = useState<AddFormKind>('favorite');
    const [draftName, setDraftName] = useState('');
    const [draftNameError, setDraftNameError] = useState('');
    const [draftComment, setDraftComment] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);

    const editingRowIdRef = useRef<string | null>(null);
    editingRowIdRef.current = editingRowId;
    const logoIcon = listTab === 'favorite' ? StarFill : Ban;

    useEffect(() => {
        setEditingRowId(null);
    }, [listTab, setEditingRowId]);

    const onStartEdit = useCallback(
        (rowId: string) => {
            const prev = editingRowIdRef.current;

            if (prev !== null && prev !== rowId) {
                onFinishEdit(prev);
            }

            setEditingRowId(rowId);
        },
        [onFinishEdit, setEditingRowId]
    );

    const tabItems = useMemo(
        () => [
            {
                id: 'favorite' as const,
                title: (
                    <span className={b('tab-label')}>
                        <Icon data={Star} size={16} className={b('tab-icon')} />
                        <Text
                            variant={isMobileScreen ? 'body-1' : 'subheader-3'}
                            className={b('tab-title')}
                        >
                            Избранные прайсы
                        </Text>
                    </span>
                )
            },
            {
                id: 'ban' as const,
                title: (
                    <span className={b('tab-label')}>
                        <Icon data={Ban} size={16} className={b('tab-icon')} />
                        <Text
                            variant={isMobileScreen ? 'body-1' : 'subheader-3'}
                            className={b('tab-title')}
                        >
                            Бан-прайсы
                        </Text>
                    </span>
                )
            }
        ],
        []
    );

    const openAddPanel = () => {
        setAddFormKind(listTab === 'favorite' ? 'favorite' : 'ban');
        setDraftName('');
        setDraftComment('');
        setDraftNameError('');
        setAddPanelOpen(true);
    };

    const closeAddPanel = () => {
        setAddPanelOpen(false);
        setDraftNameError('');
    };

    const handleAddSubmit = async () => {
        const value = draftName.trim();

        if (!value) {
            setDraftNameError('Обязательное поле');
            return;
        }

        try {
            setIsAdding(true);

            await onAdd({
                kind: addFormKind,
                value,
                comment: draftComment.trim()
            });

            setDraftName('');
            setDraftComment('');
            setDraftNameError('');
            closeAddPanel();
        } catch (error) {
            console.error('add preference error', error);
        } finally {
            setIsAdding(false);
        }
    };

    const closeRemoveModal = () => {
        setRemoveModal(null);
    };

    const openRemoveModal = (rowId: string) => {
        setRemoveModal({list: listTab, rowId});
    };

    const confirmRemoveRow = async () => {
        if (!removeModal) {
            return;
        }

        try {
            setIsRemoving(true);

            await onRemove(removeModal.rowId);

            setEditingRowId((cur) => (cur === removeModal.rowId ? null : cur));
            closeRemoveModal();
        } catch (error) {
            console.error('remove preference error', error);
        } finally {
            setIsRemoving(false);
        }
    };

    const removeModalTitle =
        removeModal === null
            ? ''
            : removeModal.list === 'favorite'
              ? 'Удалить прайс из Избранного?'
              : 'Удалить прайс из Бан-прайсов?';

    return (
        <div className={b()}>
            <div className={b('columns', {withSide: addPanelOpen})}>
                <div className={b('main')}>
                    <div className={b('toolbar')}>
                        <Tabs
                            size={isMobileScreen ? 'm' : 'xl'}
                            className={b('tabs')}
                            activeTab={listTab}
                            onSelectTab={(id) => onListTabChange(id as ListTabId)}
                            items={tabItems}
                        />

                        {isMobileScreen ? (
                            <Button size="m" view="action" onClick={openAddPanel}>
                                <Icon data={Plus} size={16} />
                            </Button>
                        ) : (
                            <Button size="s" view="action" onClick={openAddPanel}>
                                Добавить новый прайс
                            </Button>
                        )}
                    </div>

                    <div className={b('table')}>
                        {rows.length > 0 ? (
                            <>
                                <div className={b('head')}>
                                    <div className={b('head-logo')}>
                                        <Text variant="subheader-2">Лого</Text>
                                        <Icon data={CaretDown} size={16} />
                                    </div>
                                    <div className={b('head-name')}>
                                        <Text variant="subheader-2">Название для себя</Text>
                                    </div>
                                </div>

                                <KeyboardGrid rowSelector="[data-kbd-row]" tabbable>
                                    {rows.map((row) => (
                                        <div key={row.id} className={b('row')} data-kbd-row>
                                            <div className={b('cell', {logo: true})}>
                                                <Tooltip
                                                    content={
                                                        <Text
                                                            variant="body-1"
                                                            className={b(
                                                                'logoIcon-tooltip-content'
                                                            )}
                                                        >
                                                            {listTab === 'favorite'
                                                                ? 'Удалить из Избранного'
                                                                : 'Удалить из Бан-прайсов'}
                                                        </Text>
                                                    }
                                                    placement={'top-start'}
                                                    contentClassName={b('logoIcon-tooltip')}
                                                    className={b('logoIcon-tooltip-container')}
                                                    openDelay={0}
                                                    closeDelay={0}
                                                    disabled={false}
                                                >
                                                    <div
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={() => openRemoveModal(row.id)}
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key === 'Enter' ||
                                                                e.key === ' '
                                                            ) {
                                                                e.preventDefault();
                                                                openRemoveModal(row.id);
                                                            }
                                                        }}
                                                    >
                                                        <Icon
                                                            data={logoIcon}
                                                            size={16}
                                                            className={b('row-logo', {
                                                                tab: listTab
                                                            })}
                                                        />
                                                    </div>
                                                </Tooltip>

                                                <Text variant="body-2">{row.value}</Text>
                                            </div>
                                            <div className={b('cell', {comment: true})}>
                                                <RowCommentCell
                                                    row={row}
                                                    editingRowId={editingRowId}
                                                    onStartEdit={onStartEdit}
                                                    onFinishEdit={onFinishEdit}
                                                    onChangeComment={onChangeComment}
                                                    onClearComment={onClearComment}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </KeyboardGrid>
                            </>
                        ) : (
                            <Text variant="body-2" color="secondary">
                                Добавленных прайсов пока нет
                            </Text>
                        )}
                    </div>
                </div>

                {addPanelOpen && (
                    <aside className={b('side')} aria-labelledby={b('side-title')}>
                        <div className={b('side-inner')}>
                            <div className={b('side-header')}>
                                <Text id={b('side-title')} variant="subheader-3">
                                    Добавление нового прайса
                                </Text>
                                <Button
                                    view="flat"
                                    size="l"
                                    onClick={closeAddPanel}
                                    aria-label="Закрыть"
                                >
                                    <Icon data={Xmark} size={16} />
                                </Button>
                            </div>

                            <div className={b('side-body')}>
                                <RadioGroup
                                    name="price-list-kind"
                                    direction="horizontal"
                                    size="l"
                                    options={[
                                        {value: 'favorite', content: 'Избранный'},
                                        {value: 'ban', content: 'Бан-прайс'}
                                    ]}
                                    value={addFormKind}
                                    onUpdate={(v) => setAddFormKind(v as AddFormKind)}
                                />
                                <TextInput
                                    size="xl"
                                    placeholder="Название прайса"
                                    value={draftName}
                                    onUpdate={(value) => {
                                        setDraftName(value);

                                        if (draftNameError && value.trim()) {
                                            setDraftNameError('');
                                        }
                                    }}
                                    validationState={draftNameError ? 'invalid' : undefined}
                                    errorMessage={draftNameError}
                                />
                                <TextArea
                                    size="xl"
                                    minRows={3}
                                    placeholder="Комментарий (необязательно)"
                                    value={draftComment}
                                    onUpdate={setDraftComment}
                                />
                            </div>

                            <div className={b('side-footer')}>
                                <Button size="l" view="flat-secondary" onClick={closeAddPanel}>
                                    Отмена
                                </Button>
                                <Button
                                    size="l"
                                    view="action"
                                    onClick={handleAddSubmit}
                                    loading={isAdding}
                                >
                                    Добавить
                                </Button>
                            </div>
                        </div>
                    </aside>
                )}
            </div>

            <Modal
                open={Boolean(removeModal)}
                className={b('remove-modal')}
                onClose={closeRemoveModal}
            >
                <div className={b('remove-modal-header')}>
                    <Text variant="subheader-3" className={b('remove-modal-title')}>
                        {removeModalTitle}
                    </Text>
                    <Button
                        view="flat"
                        size={isMobileScreen ? 's' : 'l'}
                        onClick={closeRemoveModal}
                        aria-label="Закрыть"
                    >
                        <Icon data={Xmark} size={16} />
                    </Button>
                </div>
                <div className={b('remove-modal-actions')}>
                    <Button
                        size="l"
                        view="flat-secondary"
                        onClick={closeRemoveModal}
                        className={b('remove-modal-button')}
                    >
                        Отмена
                    </Button>
                    <Button
                        size="l"
                        view="flat-danger"
                        onClick={confirmRemoveRow}
                        loading={isRemoving}
                        className={b('remove-modal-button')}
                    >
                        Удалить
                    </Button>
                </div>
            </Modal>
        </div>
    );
};
